/**
 * Tầng chuyển "số đo thô" → "metric so với baseline" — Prompt.md §13 + BLOCKER #1 Option C.
 *
 * Đây là **ranh giới cuối cùng** mà `CameraPlacement` còn tồn tại. Từ `PostureMetrics` trở đi
 * (classifier, score, state machine, alert engine) không một dòng nào được biết placement là gì:
 * nếu logic phán xét cũng phân nhánh theo placement, mỗi lần thêm mode là phải sửa cả 5 tầng và
 * mỗi tầng là một chỗ để hai mode lệch hành vi.
 */

import type { PostureConfig } from "@studystraight/shared-config";
import type {
  CameraPlacement,
  Metric,
  PostureBaseline,
  PostureIssueType,
  PostureMeasurement,
  PostureMetricKey,
  PostureMetrics,
  VisionResult,
} from "@studystraight/shared-types";
import { METRIC_SOURCES, createMeasurer } from "@studystraight/vision-core";

const RAD_TO_DEG = 180 / Math.PI;

const UNAVAILABLE: Metric = { value: Number.NaN, available: false };

/**
 * Issue nào đo được ở placement nào (BLOCKER #1 Option C).
 *
 * Bảng này viết tay chứ không suy ra từ `METRIC_SOURCES` để nó đọc được như một đặc tả sản phẩm
 * ("mode SIDE không nhìn thấy vai lệch"), nhưng có test khẳng định hai bảng không được lệch nhau.
 */
export const ISSUE_AVAILABILITY: Record<CameraPlacement, Record<PostureIssueType, boolean>> = {
  FRONTAL: { HEAD_DOWN: true, TOO_CLOSE: true, SHOULDER_TILT: true, LEANING_FORWARD: true },
  SIDE: { HEAD_DOWN: true, TOO_CLOSE: true, SHOULDER_TILT: false, LEANING_FORWARD: true },
};

/** Mỗi issue của §13 đọc đúng một metric — bảng này thay cho chuỗi if/else rải rác. */
export const ISSUE_METRIC: Record<PostureIssueType, PostureMetricKey> = {
  HEAD_DOWN: "headAngle",
  LEANING_FORWARD: "torsoAngle",
  TOO_CLOSE: "distanceRatio",
  SHOULDER_TILT: "shoulderTilt",
};

export interface PlacementMetricExtractor {
  readonly placement: CameraPlacement;
  /**
   * Nhận `baseline` (khác chữ ký nháp trong plan): delta phải tính ở đây, vì chỉ tầng này biết
   * chiều đo nào của placement này tương ứng với metric nào. Nếu để classifier tự trừ baseline,
   * classifier buộc phải biết placement — đúng thứ Option C cố tránh.
   */
  extract(vision: VisionResult, baseline: PostureBaseline, config: PostureConfig): PostureMetrics;
}

/** Chiều đo nguồn phải `available` VÀ baseline tương ứng phải là số hữu hạn. */
function usable(source: Metric, baselineValue: number): boolean {
  return source.available && Number.isFinite(source.value) && Number.isFinite(baselineValue);
}

/** Lệch tuyệt đối so với baseline — "đã rời khỏi tư thế đã hiệu chỉnh bao nhiêu độ". */
function absoluteDelta(source: Metric, baselineValue: number): Metric {
  if (!usable(source, baselineValue)) return { ...UNAVAILABLE };
  return { value: Math.abs(source.value - baselineValue), available: true };
}

/**
 * Tỉ lệ so với baseline, lấy giá trị **thấp hơn** của hai tín hiệu khi có cả hai (DU #2).
 *
 * Vì sao lấy thấp hơn: khoảng cách hai tai co lại khi trẻ quay đầu (dương tính giả), chiều rộng
 * vai co lại khi trẻ xoay thân (dương tính giả khác). Mỗi tín hiệu sai theo một kiểu riêng, nên
 * chỉ khi CẢ HAI đều cho thấy "to hơn baseline" thì mới thực sự là ngồi gần hơn.
 */
function proximityRatio(
  primary: Metric,
  primaryBaseline: number,
  secondary: Metric,
  secondaryBaseline: number,
): Metric {
  const ratios: number[] = [];
  if (usable(primary, primaryBaseline) && primaryBaseline > 1e-9) {
    ratios.push(primary.value / primaryBaseline);
  }
  if (usable(secondary, secondaryBaseline) && secondaryBaseline > 1e-9) {
    ratios.push(secondary.value / secondaryBaseline);
  }
  if (ratios.length === 0) return { ...UNAVAILABLE };
  return { value: Math.min(...ratios), available: true };
}

/**
 * Mode FRONTAL.
 *
 * `headAngle` KHÔNG lấy từ góc tai↔vai: ở chính diện, cúi đầu gần như không làm góc đó đổi
 * (DU #4). Tín hiệu thật là khoảng hở dọc mũi↔vai co lại; ở đây nó được quy về "độ" bằng
 * `atan(drop)` để dùng chung ngưỡng `headDown` (đơn vị độ) với mode SIDE.
 *
 * `atan` — chưa hiệu chuẩn trên thiết bị thật (DU #4). Con số ~12° tương ứng đầu tụt ~0.21 lần
 * chiều rộng vai; phải đối chiếu lại bằng Debug Mode export trước khi coi ngưỡng §14 là đúng.
 */
function extractFrontal(
  measurement: PostureMeasurement,
  baseline: PostureBaseline,
): PostureMetrics {
  const drop = usable(measurement.headPosition, baseline.headPosition)
    ? Math.max(0, baseline.headPosition - measurement.headPosition.value)
    : Number.NaN;

  return {
    headAngle: Number.isFinite(drop)
      ? { value: Math.atan(drop) * RAD_TO_DEG, available: true }
      : { ...UNAVAILABLE },
    torsoAngle: absoluteDelta(measurement.torsoAngle, baseline.torsoAngle),
    shoulderTilt: absoluteDelta(measurement.shoulderTilt, baseline.shoulderTilt),
    distanceRatio: proximityRatio(
      measurement.faceScale,
      baseline.faceScale,
      measurement.shoulderWidth,
      baseline.shoulderWidth,
    ),
  };
}

/**
 * Mode SIDE.
 *
 * `headAngle` lấy trực tiếp góc tai↔vai — giá trị chính mà góc nhìn ngang mang lại. Dùng lệch
 * **tuyệt đối**: ở profile view không biết trẻ đang hướng mặt về bên nào của khung ảnh, nên
 * không phân biệt được cúi về trước với ngả về sau. Kết quả là ngả ra sau cũng bị tính — thà
 * nhắc thừa một tư thế bất thường còn hơn im lặng vì đoán sai chiều.
 */
function extractSide(measurement: PostureMeasurement, baseline: PostureBaseline): PostureMetrics {
  return {
    headAngle: absoluteDelta(measurement.headAngle, baseline.headAngle),
    torsoAngle: absoluteDelta(measurement.torsoAngle, baseline.torsoAngle),
    // Hai vai chồng nhau: không đo được, và `ISSUE_AVAILABILITY.SIDE.SHOULDER_TILT === false`.
    shoulderTilt: { ...UNAVAILABLE },
    distanceRatio: proximityRatio(
      measurement.faceScale,
      baseline.faceScale,
      { ...UNAVAILABLE },
      Number.NaN,
    ),
  };
}

type ExtractFn = (measurement: PostureMeasurement, baseline: PostureBaseline) => PostureMetrics;

function createExtractor(placement: CameraPlacement, fn: ExtractFn): PlacementMetricExtractor {
  const measurer = createMeasurer(placement);
  return {
    placement,
    extract(vision, baseline, config) {
      if (baseline.placement !== placement) {
        throw new Error(
          `Baseline của placement ${baseline.placement} không dùng được cho extractor ${placement}`,
        );
      }
      const metrics = fn(measurer.measure(vision, config), baseline);
      return applyBaselineExclusions(metrics, baseline);
    },
  };
}

/**
 * Metric đã bị calibration đánh dấu không đo được (§11) thì cả phiên cũng không đo được.
 *
 * Không có bước này, một frame may mắn nhìn thấy hông sẽ tạo ra `torsoAngle` so với một baseline
 * là `NaN` — hoặc tệ hơn, so với baseline dựng từ 3 sample nhiễu.
 */
function applyBaselineExclusions(
  metrics: PostureMetrics,
  baseline: PostureBaseline,
): PostureMetrics {
  if (baseline.unavailableMetrics.length === 0) return metrics;
  const out = { ...metrics };
  for (const key of baseline.unavailableMetrics) out[key] = { ...UNAVAILABLE };
  return out;
}

const EXTRACTORS: Record<CameraPlacement, PlacementMetricExtractor> = {
  FRONTAL: createExtractor("FRONTAL", extractFrontal),
  SIDE: createExtractor("SIDE", extractSide),
};

export function createMetricExtractor(placement: CameraPlacement): PlacementMetricExtractor {
  return EXTRACTORS[placement];
}

/**
 * Issue không thể quan sát trong cả phiên — dùng cho `StudySession.excludedIssues` (§22).
 * Report phải phân biệt "0 lần" với "không đo được"; nói với phụ huynh rằng trẻ không hề nghiêng
 * người trong khi thực ra ta không nhìn thấy là kiểu sai tệ hơn im lặng.
 */
export function resolveExcludedIssues(baseline: PostureBaseline): PostureIssueType[] {
  const availability = ISSUE_AVAILABILITY[baseline.placement];
  const sources = METRIC_SOURCES[baseline.placement];
  return (Object.keys(availability) as PostureIssueType[]).filter((issue) => {
    const metricKey = ISSUE_METRIC[issue];
    if (!availability[issue]) return true;
    if (baseline.unavailableMetrics.includes(metricKey)) return true;
    return sources[metricKey].length === 0;
  });
}
